import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";

import { useMutateUpdateModelSubClients } from "../../data/services/clientService";

const ManageSubClientsDialog = ({
  allSubClients,
  savedSubClients,
  oePeriodId,
  clientId,
  versionId,
  onClose,
}) => {
  const DATA_ITEM_KEY = "clientId";
  const updateSubClientsMutation = useMutateUpdateModelSubClients();
  const [selectedState, setSelectedState] = React.useState({});
  const [saveError, setSaveError] = React.useState("");

  React.useEffect(() => {
    if (savedSubClients) {
      const initialState = {};
      savedSubClients.forEach((item) => {
        initialState[item[DATA_ITEM_KEY]] = true;
      });
      setSelectedState(initialState);
    }
  }, [savedSubClients]);

  const onSelectionChange = (event) => {
    if (updateSubClientsMutation.isLoading) return;
    const id = event.dataItem[DATA_ITEM_KEY];
    setSelectedState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onHeaderSelectionChange = (event) => {
    if (updateSubClientsMutation.isLoading) return;
    const checked = event.syntheticEvent.target.checked;
    const newSelection = {};
    if (checked) {
      allSubClients.forEach((item) => {
        newSelection[item[DATA_ITEM_KEY]] = true;
      });
    }
    setSelectedState(newSelection);
  };

  const handleSaveSelection = async () => {
    setSaveError("");

    const selectedIds = Object.keys(selectedState).filter(
      (id) => selectedState[id],
    );

    const payload = {
      modelVersionId: parseInt(versionId),
      oePeriodId: parseInt(oePeriodId),
      clientId: clientId,
      childClientIds: selectedIds,
    };

    try {
      await updateSubClientsMutation.mutateAsync(payload);
      onClose();
    } catch (error) {
      setSaveError("We could not save the child client selection. Please try again.");
    }
  };

  const isSaveDisabled = React.useMemo(() => {
    if (updateSubClientsMutation.isLoading) return true;

    const currentSelectedIds = Object.keys(selectedState).filter(
      (id) => selectedState[id],
    );
    if (currentSelectedIds.length === 0) return true;

    const initialSavedIds = savedSubClients.map((item) =>
      item[DATA_ITEM_KEY].toString(),
    );
    if (currentSelectedIds.length !== initialSavedIds.length) return false;

    const hasChanges = currentSelectedIds.some(
      (id) => !initialSavedIds.includes(id.toString()),
    );
    return !hasChanges;
  }, [selectedState, savedSubClients, updateSubClientsMutation.isLoading]);

  return (
    <Dialog
      title={"Applied Child Clients"}
      onClose={updateSubClientsMutation.isLoading ? undefined : onClose}
      width={550}
    >
      <div style={{ padding: "10px 0", position: "relative" }}>
        <p style={{ fontSize: "13px", color: "#555", marginBottom: "15px" }}>
          Modify which child clients will inherit the current contribution model
          definitions:
        </p>

        {saveError && (
          <p
            style={{
              fontSize: "13px",
              color: "#B42318",
              marginBottom: "15px",
            }}
          >
            {saveError}
          </p>
        )}

        <Grid
          style={{
            height: "300px",
            opacity: updateSubClientsMutation.isLoading ? 0.6 : 1,
            pointerEvents: updateSubClientsMutation.isLoading ? "none" : "auto",
          }}
          data={allSubClients.map((item) => ({
            ...item,
            selected: !!selectedState[item[DATA_ITEM_KEY]],
          }))}
          selectedField="selected"
          dataItemKey={DATA_ITEM_KEY}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
        >
          <Column
            field="selected"
            width="50px"
            headerSelectionValue={
              allSubClients.length > 0 &&
              allSubClients.every((item) => selectedState[item[DATA_ITEM_KEY]])
            }
          />
          <Column field="clientId" title="Company ID" width="120px" />
          <Column field="clientName" title="Company Name" />
        </Grid>
      </div>

      <DialogActionsBar layout={"end"}>
        {updateSubClientsMutation.isLoading && (
          <div
            style={{
              marginRight: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "13px",
              color: "#666",
            }}
          >
            <Loader size="small" type="pulsing" themeColor="primary" />
            Saving changes...
          </div>
        )}

        <Button onClick={onClose} disabled={updateSubClientsMutation.isLoading}>
          Cancel
        </Button>
        <Button
          themeColor={"primary"}
          onClick={handleSaveSelection}
          disabled={isSaveDisabled}
        >
          Save Selection
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ManageSubClientsDialog;
